/**
 * Native tab bar handshake (roadmap item 38, 2026-09-09).
 *
 * A shell that draws its OWN iOS tab bar hosts one WKWebView PER TAB, each
 * pinned to one route and kept alive with its own scroll and state. In that
 * shell this page must NOT draw its pill nav — two tab bars would stack — and
 * must render only its own tab's panel.
 *
 * How the page learns it, and why it is a global rather than a bridge call:
 * the shell injects a `WKUserScript` at document start, so the answer is
 * available SYNCHRONOUSLY on the first render and the pill never flashes before
 * being hidden. There is deliberately no handshake field carrying the same
 * answer: one BridgeHandlers serves all three web views, so it has no per-view
 * answer to give, and a second channel for one bit is a second thing to keep
 * true.
 *
 * BOTH conditions are required to hide the pill: a real native shell AND the
 * flag. That is what keeps swimset.club (no bridge, no flag) and every older
 * shell (bridge, no flag) drawing their own tab bar, with no code path in
 * common. The other direction of the same skew — a NEW shell that painted an
 * OLD web bundle — is closed on the native side: the shell reads the painted
 * bundle's `x-swimset-native-tabs` meta and, when it is absent, shows no native
 * bar and injects no flag, so this file falls through to the pill.
 */
import { parseTabId, type TabId } from "./nav";
import { hasNativeShell } from "../bridge/client";

export interface ShellDescriptor {
  /** True only on a shell that draws the native tab bar and pins this view to one tab. */
  nativeTabs?: boolean;
  /** Which tab THIS web view is pinned to. A string, not a TabId: an older
   *  shell writes "scores" here (read as Sets, see parseTabId). */
  tab?: string;
}

declare global {
  interface Window {
    /** Injected by the native shell's documentStart user script. Absent everywhere else. */
    __SWIMSET_SHELL__?: ShellDescriptor;
  }
}

function descriptor(): ShellDescriptor | null {
  if (typeof window === "undefined") return null;
  const d = window.__SWIMSET_SHELL__;
  return d && typeof d === "object" ? d : null;
}

/**
 * True when the NATIVE tab bar is driving navigation: a real bridge handler AND
 * the shell's flag. Never true in a browser; never true on an older shell.
 */
export function nativeTabsActive(): boolean {
  return hasNativeShell() && descriptor()?.nativeTabs === true;
}

/**
 * Which tab this web view is pinned to, or null when the native tab bar is not
 * driving (a browser, or an older shell — where one view renders every tab).
 * An unrecognized tab id degrades to "swim" rather than rendering nothing.
 */
export function shellTab(): TabId | null {
  if (!nativeTabsActive()) return null;
  return parseTabId(descriptor()?.tab) ?? "swim";
}

/** TEST-ONLY: install or clear the shell descriptor. */
export function _setShellDescriptor(d: ShellDescriptor | null): void {
  if (d === null) delete window.__SWIMSET_SHELL__;
  else window.__SWIMSET_SHELL__ = d;
}
